import {
  ArrowDownIcon,
  Box,
  DeleteIcon,
  Divider,
  DoubleDownArrowIcon,
  EditIcon,
  FormControl,
  FormControlLabel,
  GKButton,
  GKClientDataGrid,
  GKRadio,
  GKSearchSelect,
  GKSideModal,
  GKTextField,
  GKTypography,
  Grid2,
  IconButton,
  PlusIcon,
  Popover,
  RadioGroup,
  ReloadIcon,
  RightIcon,
  Show,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  UpDownArrowIcon,
  UploadIcon,
} from "components";
import dayjs from "dayjs";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import { sum } from "lodash";
import { useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useAppSelector } from "store/store";
import { numberToCurrency } from "utils/MathFunction";
import axiosInstance from "utils/axiosInstance";
import { exportToExcel } from "utils/exportCsv";
import { handleReturnNaturalRows } from "utils/formHelpers";
import { refetchQuery } from "utils/helpers";
import { v4 as uuid } from "uuid";
import AddDeleteCashModal from "./AddDeleteCashModal";
import ImportModelPortfolio from "./ImportModelPortfolio";
import RebalanceRationale from "./RebalanceRationale";
import UpdateNextRebalanceDate from "./UpdateNextRebalanceDate";
import UpdateRebalanceDate from "./UpdateRebalanceDate";
import VersionUploadModal from "./VersionUploadModal";

const RebalanceModal = ({
  setOpen,
  open,
  selectedId: selectedModelId,
  isEditModal = false,
  rebalanceId,
}: {
  setOpen: (open: boolean) => void;
  open: boolean;
  selectedId: any;
  isEditModal?: boolean;
  rebalanceId?: any;
}) => {
  const { id, modelPortfolioId } = useParams();

  const companies = useAppSelector((state) => state?.app.companies);

  const selectedId = modelPortfolioId || selectedModelId;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openMenu = Boolean(anchorEl);

  const [openUploadModal, setOpenUploadModal] = useState(false);

  const [opeRationaleModal, setOpeRationaleModal] = useState(false);

  const [importSheetModal, setImportSheetModal] = useState(false);

  const [opeRationaleIndex, setOpeRationaleIndex] = useState(0);

  const [openRebalanceDateModal, setOpenRebalanceDateModal] = useState(false);

  const [rebalanceUpdateType, setRebalanceUpdateType] = useState("");

  const [openNextRebalanceDateModal, setOpenNextRebalanceDateModal] =
    useState(false);

  const [openCashModal, setOpenCashModal] = useState(false);

  const [totalWeightage, setTotalWeightage] = useState(0);

  const form = useForm({
    defaultValues: {
      transactionType: "DEPOSIT",
      rebalanceValue: "",
      nextRebalanceDate: "",
      equalWeightage: "false",
      versionName: "",
      cash: 0,
      date: dayjs() as any,
      startValue: "",
      minimumInvestement: "",
      remarks: "",
      totalWeightage: 0,
      oldStocks: [],
      stocks: [
        {
          isinCode: "",
          allocation: null,
          targetPrice: null,
          currentAllocation: null,
          stopLoss: null,
          weightage: null,
          rationale: "",
          sector: "",
          gridCode: "",
          cap: "",
          companyName: "",
          uuid: uuid(),
        },
      ],
    },
  });

  const { data: modelPortfolioData, isFetching } = useFetchQuery({
    key: ["GET_VERSION_BY_ID", selectedId],
    route: rebalanceId
      ? `model_portfolio/${id}/version/${selectedId}/rebalance/?rebalance_id=${rebalanceId}`
      : `model_portfolio/${id}/version/${selectedId}/`,
    enabled: Boolean(selectedId),
    success: (response: any) => {
      form.setValue("versionName", response?.data?.version);
      form.setValue("nextRebalanceDate", response?.data?.nextRebalanceDate);
      form.setValue("startValue", response?.data?.startValue);
      form.setValue("minimumInvestement", response?.data?.minimumInvestement);
      form.setValue(
        "stocks",
        response?.data?.allocItem?.map((item: any) => {
          return {
            ...item,
            uuid: uuid(),
          };
        }),
      );
      setTotalWeightage(
        sum(response?.data?.allocItem?.map((d: any) => +d?.allocation)),
      );
      form.setValue(
        "oldStocks",
        response?.data?.allocItem?.map((item: any) => {
          return {
            ...item,
            uuid: uuid(),
          };
        }),
      );
    },
  });

  const { isLoading, mutate } = useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(
        `/model_portfolio/${id}/version/${modelPortfolioData?.data?.id}/rebalance/`,
        data,
      ),
    onSuccess: () => {
      refetchQuery("Model Rebalance");
      refetchQuery("GET_REBALANCES");
      refetchQuery("GET_REBALANCES_DETAILS");
      setOpen(false);
    },
  });

  const { isLoading: versionUpdateLoading, mutate: updateVersion } =
    useMutation({
      mutationFn: (data: any) =>
        axiosInstance.patch(
          `/model_portfolio/${id}/version/${modelPortfolioData?.data?.id}/rebalance/`,
          data,
        ),
      onSuccess: () => {
        refetchQuery("Model Rebalance");
        refetchQuery("GET_REBALANCES");
        refetchQuery("GET_REBALANCES_DETAILS");
        setOpen(false);
      },
    });

  const { insert, replace } = useFieldArray({
    control: form.control,
    name: "stocks",
  });

  const handleSubmitFun = (data: any) => {
    const apiData = {
      versionName: data?.versionName,
      version: modelPortfolioData?.data?.id,
      cash: Number(data?.cash),
      date: dayjs(data?.date).format("YYYY-MM-DD"),
      nextRebalanceDate: dayjs(data?.nextRebalanceDate).format("YYYY-MM-DD"),
      transactionType: data?.transactionType,
      remarks: data?.remarks,
      isDraft: false,
      rebalancedVersion: {
        allocItem: data?.stocks,
      },
    };
    mutate(apiData);
  };

  const companyOptions = useMemo(
    () =>
      handleReturnNaturalRows({
        options: companies?.map((item: any) => ({
          ...item,
          name: item?.companyName,
          value: item?.isin,
          gridCode: item?.gridCode,
        })),
        values: form.getValues("stocks"),
        selectedValueKey: "isinCode",
        optionsValueKey: "isin",
      }) || [],
    [companies, form],
  );

  return (
    <GKSideModal
      onClose={() => {
        setOpen(false);
        form.reset();
      }}
      width="100%"
      setOpen={setOpen}
      open={open}
      modalTitle={"Model Rebalance"}
      footerButton={
        <Grid2 container justifyContent={"space-between"} gap={1}>
          <GKButton
            variant="outlined"
            color="dark"
            onClick={() =>
              exportToExcel(
                form.getValues("stocks")?.map((d) => {
                  return {
                    "Company Name": d?.companyName,
                    "Allocated Weightage": d?.allocation,
                    "Current Allocation": d?.currentAllocation,
                    Cap: d?.cap,
                  };
                }),
                `${form.getValues("versionName")}_Rebalance`,
              )
            }
          >
            Download Excel
          </GKButton>
          <Show.When isTrue={!isEditModal}>
            <GKButton
              variant="lightBg"
              size="medium"
              loading={isLoading}
              sx={{ ml: "auto" }}
              onClick={() => {
                const data = form.getValues();
                const apiData = {
                  versionName: data?.versionName,
                  version: modelPortfolioData?.data?.id,
                  cash: Number(data?.cash),
                  date: dayjs(data?.date).format("YYYY-MM-DD"),
                  nextRebalanceDate: dayjs(data?.nextRebalanceDate).format(
                    "YYYY-MM-DD",
                  ),
                  transactionType: data?.transactionType,
                  remarks: data?.remarks,
                  rebalancedVersion: {
                    allocItem: data?.stocks,
                  },
                  isDraft: true,
                };
                mutate(apiData);
              }}
            >
              {"Save as Draft"}
            </GKButton>
            <GKButton
              variant="contained"
              type="submit"
              size="medium"
              form="equity-basket-rebalance-form"
              loading={isLoading}
            >
              {"Draft & Rebalance"}
            </GKButton>
          </Show.When>
          <Show.When isTrue={isEditModal}>
            <GKButton
              variant="lightBg"
              size="medium"
              loading={versionUpdateLoading}
              sx={{ ml: "auto" }}
              onClick={() => {
                const data = form.getValues();
                const apiData = {
                  versionName: data?.versionName,
                  version: modelPortfolioData?.data?.id,
                  cash: Number(data?.cash),
                  date: dayjs(data?.date).format("YYYY-MM-DD"),
                  nextRebalanceDate: dayjs(data?.nextRebalanceDate).format(
                    "YYYY-MM-DD",
                  ),
                  transactionType: data?.transactionType,
                  remarks: data?.remarks,
                  rebalancedVersion: {
                    allocItem: data?.stocks,
                  },
                  rebalanceId: rebalanceId,
                  isDraft: true,
                };
                updateVersion(apiData);
              }}
            >
              Update{" "}
            </GKButton>
          </Show.When>
        </Grid2>
      }
    >
      <form
        onSubmit={form.handleSubmit(handleSubmitFun)}
        id="equity-basket-rebalance-form"
      >
        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <TableContainer
              sx={{
                "& .MuiTableCell-root": {
                  width: `${100 / 7}%`,
                  bgcolor: "#F8F8F8",
                  padding: "16px",
                },
                border: "none",
              }}
            >
              <Table>
                <TableBody>
                  <TableRow>
                    <TableCell>
                      <Box display={"flex"} gap={2} alignItems={"flex-start"}>
                        <Box>
                          <GKTypography
                            loading={isFetching}
                            fontWeight={400}
                            fontSize={12}
                            color={"var(--neutral-500)"}
                          >
                            Date of Rebalance
                          </GKTypography>
                          <GKTypography
                            loading={isFetching}
                            fontWeight={600}
                            fontSize={12}
                            color={"var(--black)"}
                          >
                            {form.getValues("date") === null ||
                            form.getValues("date") === "Invalid Date"
                              ? "-"
                              : dayjs(form.getValues("date"))?.format(
                                  "DD MMM YYYY",
                                )}
                          </GKTypography>
                        </Box>
                        <IconButton
                          sx={{ p: 0 }}
                          onClick={() => {
                            setRebalanceUpdateType("nextRebalanceDate");
                            setOpenRebalanceDateModal(true);
                          }}
                        >
                          <EditIcon
                            width={16}
                            height={16}
                            color="var(--neutral-900)"
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display={"flex"} gap={2} alignItems={"flex-start"}>
                        <Box>
                          <GKTypography
                            loading={isFetching}
                            fontWeight={400}
                            fontSize={12}
                            color={"var(--neutral-500)"}
                          >
                            Version{" "}
                          </GKTypography>
                          <GKTypography
                            loading={isFetching}
                            fontWeight={600}
                            fontSize={12}
                            color={"var(--black)"}
                          >
                            {modelPortfolioData?.data?.version || "-"}
                          </GKTypography>
                        </Box>
                        <IconButton
                          sx={{ p: 0 }}
                          onClick={() => {
                            setRebalanceUpdateType("nextRebalanceDate");
                            setOpenRebalanceDateModal(true);
                          }}
                        >
                          <EditIcon
                            width={16}
                            height={16}
                            color="var(--neutral-900)"
                          />
                        </IconButton>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <GKTypography
                        loading={isFetching}
                        fontWeight={400}
                        fontSize={12}
                        color={"var(--neutral-500)"}
                      >
                        Portfolio Value
                      </GKTypography>
                      <GKTypography
                        loading={isFetching}
                        fontWeight={600}
                        fontSize={12}
                      >
                        {modelPortfolioData?.data?.portfolioValue
                          ? numberToCurrency(
                              modelPortfolioData?.data?.portfolioValue,
                            )
                          : "-"}
                      </GKTypography>
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell />
                    <TableCell>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <GKButton
                          onClick={() => {
                            setOpenCashModal(true);
                          }}
                          startIcon={<UpDownArrowIcon />}
                          sx={{ background: "var(--white)" }}
                          variant="outlined"
                          color="dark"
                        >
                          Deposit or Withdraw Amount
                        </GKButton>
                        <GKButton
                          variant="contained"
                          startIcon={<UploadIcon />}
                          endIcon={<ArrowDownIcon />}
                          onClick={(event) => {
                            setAnchorEl(event.currentTarget);
                          }}
                        >
                          Upload Via
                        </GKButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid2>
          <Grid2 size={12} display={"flex"} alignItems={"center"} gap={1}>
            <Typography>Weightage Type</Typography>
            <Controller
              control={form.control}
              name="equalWeightage"
              render={({ field }) => {
                return (
                  <FormControl
                    sx={{
                      "& .MuiFormGroup-root.MuiRadioGroup-root": {
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                      },
                    }}
                  >
                    <RadioGroup
                      {...field}
                      onChange={(eve, val) => {
                        field.onChange(val);

                        if (JSON.parse(form.getValues("equalWeightage"))) {
                          const equalAllocation =
                            100 / form.getValues("stocks")?.length;
                          const newData = form
                            .getValues("stocks")
                            ?.map((da) => {
                              return {
                                ...da,
                                allocation: equalAllocation,
                                weightage: equalAllocation,
                              };
                            });
                          form.setValue("stocks", newData);
                        } else {
                          const da = form.getValues("stocks")?.filter(
                            (d) =>
                              !form
                                .getValues("oldStocks")
                                ?.map((inD) => inD?.isinCode)
                                ?.includes(d?.isinCode),
                          );

                          form.setValue(
                            "stocks",
                            [...form.getValues("oldStocks"), ...da]?.map(
                              (d) => {
                                return {
                                  ...d,
                                  allocation: 0,
                                  weightage: 0,
                                };
                              },
                            ),
                          );
                        }
                      }}
                    >
                      <FormControlLabel
                        value={false}
                        control={<GKRadio />}
                        label="Custom Weighted"
                      />
                      <FormControlLabel
                        value
                        control={<GKRadio />}
                        label="Equal Weighted"
                      />
                    </RadioGroup>
                  </FormControl>
                );
              }}
            />
          </Grid2>
          <Grid2 size={12}>
            <GKClientDataGrid
              maxHeight={"calc(100vh - 340px)"}
              name="addModelPortfoliol"
              getRowId={(row) => row.uuid}
              loading={isFetching}
              disableRowSelectionOnClick
              rows={
                form.getValues("stocks")?.map((d, index) => {
                  return {
                    ...d,
                    uuidIndex: index,
                  };
                }) || []
              }
              columns={[
                {
                  minWidth: 550,
                  field: "stock",
                  disableColumnMenu: true,
                  valueGetter: (params) => {
                    return params?.row?.companyName || "";
                  },
                  renderCell: (params) => {
                    const index = params.row.uuidIndex;
                    return (
                      <Controller
                        rules={{
                          required: true,
                        }}
                        control={form.control}
                        name={`stocks.${index}.companyName`}
                        render={({ field, fieldState: { error } }) => {
                          return (
                            <GKSearchSelect
                              {...field}
                              size="small"
                              disableClearable={false}
                              renderOption={(props, option) => (
                                <li
                                  {...props}
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <Typography
                                    color={"var(--white)"}
                                    fontSize={8}
                                    style={{
                                      background:
                                        option?.instType === "EQ"
                                          ? "var(--secondary-900)"
                                          : "var(--orange-700)",
                                      padding: "2px 4px",
                                      borderRadius: 4,
                                    }}
                                  >
                                    {option?.instType}
                                  </Typography>
                                  <Typography whiteSpace={"nowrap"}>
                                    {option?.name}
                                  </Typography>
                                </li>
                              )}
                              sx={{
                                width: "100%",
                              }}
                              options={companyOptions || []}
                              getOptionLabel={(option) => option.name || option}
                              onChange={(e, val) => {
                                form.setValue(
                                  `stocks.${index}.isinCode`,
                                  val?.isin,
                                );
                                form.setValue(`stocks.${index}.cap`, val?.cap);
                                form.setValue(
                                  `stocks.${index}.gridCode`,
                                  val?.gridCode,
                                );
                                form.setValue(
                                  `stocks.${index}.companyName`,
                                  val?.name,
                                );
                                form.setValue(
                                  `stocks.${index}.sector`,
                                  val?.sector,
                                );
                              }}
                              error={!!error}
                            />
                          );
                        }}
                      />
                    );
                  },
                },
                {
                  flex: 1,
                  disableColumnMenu: true,
                  field: "weightage",
                  headerName: `Weightage (${totalWeightage?.toFixed(2)})`,
                  type: "number",
                  valueGetter: (params) => {
                    return params?.row?.allocation
                      ? +params?.row?.allocation
                      : 0;
                  },
                  renderCell: (params) => {
                    const index = params.row.uuidIndex;

                    return (
                      <Controller
                        control={form.control}
                        name={`stocks.${index}.allocation`}
                        rules={{
                          required: true,
                        }}
                        render={({ field, fieldState: { error } }) => {
                          return form.getValues("equalWeightage") === "true" ? (
                            <Typography>
                              {form
                                ?.getValues(`stocks.${index}.allocation`)
                                ?.toFixed(2)}
                            </Typography>
                          ) : (
                            <GKTextField
                              {...field}
                              size="small"
                              name={`stocks.${index}.allocation`}
                              type="number"
                              value={form.getValues(
                                `stocks.${index}.allocation`,
                              )}
                              placeholder="Weightage"
                              onChange={(e) => {
                                form.setValue(`stocks.${index}`, {
                                  ...form.getValues(`stocks.${index}`),
                                  allocation: +e.target.value,
                                  weightage:
                                    (+form.getValues("startValue") /
                                      +e.target.value) *
                                    100,
                                });
                              }}
                              error={!!error}
                            />
                          );
                        }}
                      />
                    );
                  },
                },
                {
                  field: "copyAllocaton",
                  headerName: "Current allocation",
                  align: "center",
                  type: "number",
                  flex: 1,
                  sortable: false,
                  disableColumnMenu: true,
                  renderCell: (params) => {
                    return (
                      <Box
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"space-between"}
                        width={"100%"}
                      >
                        <IconButton
                          onClick={() => {
                            const newData = form
                              .getValues("stocks")
                              ?.map((dataItem) => {
                                return {
                                  ...dataItem,
                                  allocation:
                                    dataItem?.isinCode === params?.row?.isinCode
                                      ? params?.row?.currentAllocation
                                      : dataItem?.allocation,
                                };
                              });

                            form.setValue("stocks", newData);
                          }}
                        >
                          <DoubleDownArrowIcon
                            style={{
                              transform: "rotate(90deg)",
                            }}
                          />
                        </IconButton>
                        <Typography>
                          {params?.row?.currentAllocation}
                        </Typography>
                      </Box>
                    );
                  },
                },
                {
                  field: "rationale",
                  headerName: "rationale",
                  disableColumnMenu: true,
                  sortable: false,
                  flex: 1,
                  renderCell: (params) => {
                    const index = params.row.uuidIndex;
                    return (
                      <GKButton
                        size="small"
                        onClick={() => {
                          setOpeRationaleIndex(index);
                          setOpeRationaleModal(true);
                        }}
                        endIcon={
                          form.getValues(`stocks.${index}.rationale`) && (
                            <RightIcon />
                          )
                        }
                        startIcon={
                          !form.getValues(`stocks.${index}.rationale`) && (
                            <PlusIcon />
                          )
                        }
                      >
                        {form.getValues(`stocks.${index}.rationale`)
                          ? "View"
                          : "Add Rationale"}
                      </GKButton>
                    );
                  },
                },
                {
                  field: "action",
                  headerName: "Actions",
                  disableColumnMenu: true,
                  align: "right",
                  headerAlign: "right",
                  sortable: false,
                  renderCell: (params) => {
                    const index = params.row.uuidIndex;
                    return (
                      <IconButton
                        disabled={form.getValues("stocks").length === 1}
                        onClick={() => {
                          const equalAllocation =
                            100 /
                            form
                              .getValues("stocks")
                              ?.filter((d, i) => i !== index)?.length;

                          const updatedData = JSON.parse(
                            form.getValues("equalWeightage"),
                          )
                            ? form
                                .getValues("stocks")
                                ?.filter((d, i) => i !== index)
                                ?.map((da) => {
                                  return {
                                    ...da,
                                    allocation: equalAllocation,
                                    weightage: equalAllocation,
                                  };
                                })
                            : form
                                .getValues("stocks")
                                ?.filter((d, i) => i !== index);
                          setTotalWeightage(
                            sum(updatedData?.map((d) => +d?.allocation)),
                          );
                          form.setValue("stocks", updatedData);
                        }}
                        color="error"
                        className="outline"
                      >
                        <DeleteIcon />
                      </IconButton>
                    );
                  },
                },
              ]}
              headerComponent={
                <Grid2
                  gap={1}
                  container
                  alignItems={"center"}
                  justifyContent={"flex-end"}
                >
                  <Typography fontSize={12} color={"var(--grey-700)"}>
                    Total Weightage:{"  "}
                    <span style={{ fontWeight: 600, color: "var(--grey-900)" }}>
                      {totalWeightage?.toFixed(2)}
                    </span>
                  </Typography>
                  <IconButton
                    onClick={() => {
                      form.trigger();
                      setTotalWeightage(
                        sum(
                          form.getValues("stocks")?.map((d) => +d?.allocation),
                        ),
                      );
                    }}
                  >
                    <ReloadIcon
                      width={14}
                      height={14}
                      color="var(--primary-900)"
                    />
                  </IconButton>
                  <Divider orientation="vertical" sx={{ height: 24 }} />{" "}
                  <GKButton
                    onClick={() => {
                      insert(0, {
                        isinCode: "",
                        allocation: null,
                        targetPrice: null,
                        stopLoss: null,
                        weightage: null,
                        currentAllocation: null,
                        rationale: "",
                        sector: "",
                        gridCode: "",
                        cap: "",
                        companyName: "",
                        uuid: uuid(),
                      });
                      if (JSON.parse(form.getValues("equalWeightage"))) {
                        const equalAllocation =
                          100 / form.getValues("stocks")?.length;
                        const newData = form.getValues("stocks")?.map((da) => {
                          return {
                            ...da,
                            allocation: equalAllocation,
                            weightage: equalAllocation,
                          };
                        });
                        setTotalWeightage(
                          sum(newData?.map((d) => +d?.allocation)),
                        );
                        replace(newData);
                      }
                    }}
                    size="small"
                    startIcon={<PlusIcon />}
                  >
                    Add Stock
                  </GKButton>
                </Grid2>
              }
            />
          </Grid2>
        </Grid2>
      </form>

      <Show.When isTrue={openUploadModal}>
        <VersionUploadModal
          open={openUploadModal}
          form={form}
          setOpen={setOpenUploadModal}
        />
      </Show.When>

      <Show.When isTrue={openCashModal}>
        <AddDeleteCashModal
          open={openCashModal}
          form={form}
          setOpen={setOpenCashModal}
        />
      </Show.When>
      <Show.When isTrue={openRebalanceDateModal}>
        <UpdateRebalanceDate
          open={openRebalanceDateModal}
          form={form}
          setOpen={setOpenRebalanceDateModal}
          selectedId={selectedId}
          rebalanceUpdateType={rebalanceUpdateType}
          id={id}
        />
      </Show.When>
      <Show.When isTrue={openNextRebalanceDateModal}>
        <UpdateNextRebalanceDate
          open={openNextRebalanceDateModal}
          form={form}
          setOpen={setOpenNextRebalanceDateModal}
        />
      </Show.When>
      <Show.When isTrue={importSheetModal}>
        <ImportModelPortfolio
          setOpen={setImportSheetModal}
          open={importSheetModal}
          isRebalanceModal
          setRebalanceModal={setOpen}
          rebalanceData={selectedId}
        />
      </Show.When>
      <Show.When isTrue={opeRationaleModal}>
        <RebalanceRationale
          open={opeRationaleModal}
          index={opeRationaleIndex}
          form={form}
          setOpen={setOpeRationaleModal}
        />
      </Show.When>
      <Popover
        id={id}
        open={openMenu}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Grid2
          container
          gap={1}
          alignItems={"center"}
          justifyContent={"center"}
          flexDirection={"column"}
        >
          <GKButton
            fullWidth
            onClick={() => {
              setImportSheetModal(true);
            }}
            endIcon={<RightIcon />}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              color: "var(--black)",
            }}
          >
            Trades
          </GKButton>
          <GKButton
            fullWidth
            onClick={() => {
              setOpenUploadModal(true);
            }}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              color: "var(--black)",
            }}
            endIcon={<RightIcon />}
          >
            Weightage
          </GKButton>
        </Grid2>{" "}
      </Popover>
    </GKSideModal>
  );
};

export default RebalanceModal;
